// Derived from dojo/aspect — Copyright (c) The JS Foundation, BSD-3-Clause. See ../../LICENSE.
// mds-dojo — dojo/aspect. Faithful port of dojo/aspect (linked-list dispatcher),
// so the subtle semantics the real code relies on all hold:
//   - before: advices run in REVERSE registration order; a returned array replaces
//     the args, undefined keeps them.
//   - after: advices run in registration order; without receiveArguments the advice
//     gets (returnValue, originalArgs) and its non-undefined return replaces the
//     result; with receiveArguments it gets the original args and only a
//     non-undefined return replaces the result.
//   - removal is safe mid-dispatch (linked list + advice-nulling), and advices added
//     during a dispatch don't run in that dispatch (executionId guard).
// dojo/Evented.on builds on after(); dojo widgets + SectionLite use after().

interface Signal {
  remove: () => void;
  id?: number;
  advice?: Function | null;
  receiveArguments?: boolean;
  next?: Signal;
  previous?: Signal;
}

function advise(dispatcher: any, type: string, advice: any, receiveArguments?: boolean): Signal {
  const previous = dispatcher[type];
  const around = type === 'around';
  let signal: Signal;

  if (around) {
    let advised: Function | null = advice((...a: any[]) => previous.advice(this, a));
    signal = {
      remove() { if (advised) { advised = dispatcher = advice = null as any; } },
      advice(target: any, args: any) {
        return advised ? advised.apply(target, args) : previous.advice(target, args);
      },
    } as any;
  } else {
    signal = {
      remove() {
        if (signal.advice) {
          const prev = signal.previous;
          const next = signal.next;
          if (!next && !prev) {
            delete dispatcher[type];
          } else {
            if (prev) { prev.next = next; } else { dispatcher[type] = next; }
            if (next) { next.previous = prev; }
          }
          dispatcher = advice = signal.advice = null as any;
        }
      },
      id: dispatcher.nextId++,
      advice,
      receiveArguments,
    };
  }

  if (previous && !around) {
    if (type === 'after') {
      let p = previous;
      while (p.next && (p = p.next)) { /* walk to end */ }
      p.next = signal;
      signal.previous = p;
    } else if (type === 'before') {
      dispatcher[type] = signal;
      signal.next = previous;
      previous.previous = signal;
    }
  } else {
    dispatcher[type] = signal;
  }
  return signal;
}

function makeAspect(type: string) {
  return function (target: any, methodName: string, advice: any, receiveArguments?: boolean): Signal {
    const existing = target[methodName];
    let dispatcher: any;
    if (!existing || existing.target !== target) {
      target[methodName] = dispatcher = function (this: any) {
        const executionId = dispatcher.nextId;
        let args: any = arguments;
        let before = dispatcher.before;
        while (before) {
          if (before.advice) { args = before.advice.apply(this, args) || args; }
          before = before.next;
        }
        let results: any;
        if (dispatcher.around) { results = dispatcher.around.advice(this, args); }
        let after = dispatcher.after;
        while (after && after.id < executionId) {
          if (after.advice) {
            if (after.receiveArguments) {
              const newResults = after.advice.apply(this, args);
              results = newResults === undefined ? results : newResults;
            } else {
              results = after.advice.call(this, results, args);
            }
          }
          after = after.next;
        }
        return results;
      };
      if (existing) {
        dispatcher.around = { advice: (t: any, a: any) => existing.apply(t, a) };
      }
      dispatcher.target = target;
      dispatcher.nextId = dispatcher.nextId || 0;
    }
    return advise(dispatcher || existing, type, advice, receiveArguments);
  };
}

export const after = makeAspect('after');
export const before = makeAspect('before');
export const around = makeAspect('around');

const aspect = { after, before, around };
export default aspect;
